import { createClient } from "@/app/utils/supabase/client";
import { ProjectInsert, ProjectUpdate, ProjectDocument } from "@/app/lib/supabase/types";
import { SUPABASE_TABLES } from "../supabase/supabaseDBConstants";

export const projectsApi = {
  /**
   * Fetch all projects for the current company
   * RLS policies will automatically filter based on user role
   */
  async getProjects(companyId: string, userId?: string, userRole?: string) {
    const supabase = createClient();
    
    let query = supabase
      .from(SUPABASE_TABLES.PROJECTS)
      .select("*")
      .eq("company_id", companyId)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    // If user is quality_control, we need to filter by allowed projects
    if (userRole === 'quality_control' && userId) {
      // First get allowed production stages for this user
      const { data: allowedStages } = await supabase
        .from(SUPABASE_TABLES.PRODUCTION_STAGE_ALLOWED_USERS)
        .select("production_stage_id")
        .eq("user_id", userId)
        .eq("company_id", companyId);

      if (allowedStages && allowedStages.length > 0) {
        // Get production stages to find project IDs
        const stageIds = allowedStages.map(s => s.production_stage_id);
        const { data: stages } = await supabase
          .from(SUPABASE_TABLES.PRODUCTION_STAGES)
          .select("project_id")
          .in("id", stageIds)
          .is("deleted_at", null);

        if (stages && stages.length > 0) {
          const projectIds = [...new Set(stages.map(s => s.project_id))];
          query = query.in("id", projectIds);
        } else {
          // No accessible projects
          return [];
        }
      } else {
        // No accessible projects
        return [];
      }
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching projects:", error);
      throw error;
    }

    return data || [];
  },

  /**
   * Fetch active (not closed) projects
   */
  async getActiveProjects(companyId: string, userId?: string, userRole?: string) {
    const projects = await this.getProjects(companyId, userId, userRole);
    return projects.filter(p => !p.is_closed);
  },

  /**
   * Fetch closed projects
   */
  async getClosedProjects(companyId: string, userId?: string, userRole?: string) {
    const projects = await this.getProjects(companyId, userId, userRole);
    return projects.filter(p => p.is_closed === true);
  },

  /**
   * Fetch a single project by ID
   */
  async getProject(projectId: string) {
    const supabase = createClient();
    
    const { data, error } = await supabase
      .from(SUPABASE_TABLES.PROJECTS)
      .select("*")
      .eq("id", projectId)
      .is("deleted_at", null)
      .single();

    if (error) {
      console.error("Error fetching project:", error);
      throw error;
    }

    return data;
  },

  /**
   * Fetch project documents
   */
  async getProjectDocuments(projectId: string): Promise<ProjectDocument[]> {
    const supabase = createClient();
    
    const { data, error } = await supabase
      .from(SUPABASE_TABLES.PROJECT_DOCUMENTS)
      .select("*")
      .eq("project_id", projectId)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching project documents:", error);
      throw error;
    }

    return data || [];
  },

  /**
   * Create a new project
   */
  async createProject(project: ProjectInsert) {
    const supabase = createClient();
    
    const { data, error } = await supabase
      .from(SUPABASE_TABLES.PROJECTS)
      .insert(project)
      .select()
      .single();

    if (error) {
      console.error("Error creating project:", error);
      throw error;
    }

    return data;
  },

  /**
   * Update an existing project
   */
  async updateProject(projectId: string, updates: ProjectUpdate) {
    const supabase = createClient();
    
    const { data, error } = await supabase
      .from(SUPABASE_TABLES.PROJECTS)
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", projectId)
      .select()
      .single();

    if (error) {
      console.error("Error updating project:", error);
      throw error;
    }

    return data;
  },

  /**
   * Soft delete a project
   */
  async deleteProject(projectId: string) {
    const supabase = createClient();
    
    const { data, error } = await supabase
      .from(SUPABASE_TABLES.PROJECTS)
      .update({
        deleted_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq("id", projectId)
      .select()
      .single();

    if (error) {
      console.error("Error deleting project:", error);
      throw error;
    }

    return data;
  },

  /**
   * Close a project
   */
  async closeProject(projectId: string) {
    const supabase = createClient();
    
    const { data, error } = await supabase
      .from(SUPABASE_TABLES.PROJECTS)
      .update({
        is_closed: true,
        updated_at: new Date().toISOString(),
      })
      .eq("id", projectId)
      .select()
      .single();

    if (error) {
      console.error("Error closing project:", error);
      throw error;
    }

    return data;
  },

  /**
   * Reopen a closed project
   */
  async reopenProject(projectId: string) {
    const supabase = createClient();
    
    const { data, error } = await supabase
      .from(SUPABASE_TABLES.PROJECTS)
      .update({
        is_closed: false,
        updated_at: new Date().toISOString(),
      })
      .eq("id", projectId)
      .select()
      .single();

    if (error) {
      console.error("Error reopening project:", error);
      throw error;
    }

    return data;
  },
};